import { COMMON_TOKEN_IDS, DIALOGS, type LockTokenId } from "@/types";
import { useDialogPortal, useLockPositions } from "@/hooks";
import { useCallback, useMemo } from "react";
import type { DepositMoreFormDialogProps, LockFormDialogProps } from "@/components";
import { usePortfolioStore } from "@/zustand/PortfolioStore";

export type PortfolioTokenCard = {
    tokenId: keyof typeof COMMON_TOKEN_IDS;
    title: string;
    titleTooltip?: string;
    subtitle: string;
    slots?: {
        buttons?: {
            primary?: {
                label: string;
                disabled?: boolean;
                onClick?: () => void;
            };
            secondary?: {
                label: string;
                disabled?: boolean;
                onClick?: () => void;
            };
        };
        footer: {
            links: (
                | {
                      label: string;
                      href: string;
                      disabled?: boolean;
                  }
                | {
                      label: string;
                      onClick: () => void;
                      disabled?: boolean;
                  }
            )[];
        };
    };
};

export const usePortfolioTokenCards = () => {
    const { openDialog } = useDialogPortal();
    const { setPositionType, setSelectedPosition } = usePortfolioStore();
    const { lockedPositions } = useLockPositions();

    const handleLock = useCallback(
        (tokenId: LockTokenId) => {
            // void tokenId;
            openDialog({
                dialogType: DIALOGS.LOCK_FORM,
                dialogProps: {
                    initialDuration: 1,
                    initialLockAmount: 0,
                    initialTokenId: tokenId,
                } satisfies LockFormDialogProps,
            });
        },
        [openDialog],
    );

    const handleStake = useCallback(() => {
        openDialog({
            dialogType: DIALOGS.DEPOSIT_MORE_FORM,
            dialogProps: {} satisfies DepositMoreFormDialogProps,
        });
    }, [openDialog]);

    const handleStakeMore = useCallback(() => {
        openDialog({
            dialogType: DIALOGS.DEPOSIT_MORE_FORM,
            dialogProps: {} satisfies DepositMoreFormDialogProps,
        });
    }, [openDialog]);

    const handleRedeem = useCallback(() => {
        openDialog({
            dialogType: DIALOGS.REDEEM_FORM,
            dialogProps: {},
        });
    }, [openDialog]);

    const handleIncreaseLockDuration = useCallback(() => {
        setPositionType("locked");
        requestAnimationFrame(() => {
            document.getElementById("positions")?.scrollIntoView({ behavior: "auto" });
        });
        if (lockedPositions?.length && lockedPositions[0]) {
            setSelectedPosition({
                type: "locked",
                position: lockedPositions[0],
                key: `locked-${0}`,
            });
        }
    }, [openDialog, lockedPositions]);

    const handleViewPositions = useCallback((type: "locked" | "staked") => {
        setPositionType(type);
        requestAnimationFrame(() => {
            document.getElementById("positions")?.scrollIntoView({ behavior: "auto" });
        });
    }, []);

    const cards = useMemo<PortfolioTokenCard[]>(
        () => [
            {
                tokenId: COMMON_TOKEN_IDS.IUSD,
                title: "iUSD (Unallocated)",
                subtitle: "Minted stablecoin",
                slots: {
                    buttons: {
                        primary: {
                            label: "Lock",
                            onClick: () => handleLock(COMMON_TOKEN_IDS.IUSD),
                        },
                        secondary: {
                            label: "Stake",
                            onClick: () => handleStake(),
                        },
                    },
                    footer: {
                        links: [
                            {
                                label: "Mint more iUSD",
                                href: "/mint",
                            },
                            {
                                label: "Redeem",
                                onClick: () => handleRedeem(),
                            },
                        ],
                    },
                },
            },
            {
                tokenId: COMMON_TOKEN_IDS.SIUSD,
                title: "siUSD",
                subtitle: "Staked iUSD",
                slots: {
                    buttons: {
                        primary: {
                            label: "Lock",
                            onClick: () => handleLock(COMMON_TOKEN_IDS.SIUSD),
                        },
                        secondary: {
                            label: "Stake more",
                            onClick: () => handleStakeMore(),
                        },
                    },
                    footer: {
                        links: [
                            {
                                label: "View positions",
                                // href: "#positions",
                                onClick: () => handleViewPositions("staked"),
                            },
                        ],
                    },
                },
            },
            {
                tokenId: COMMON_TOKEN_IDS.LIUSD,
                title: "liUSD",
                subtitle: "Locked iUSD",
                slots: {
                    footer: {
                        links: [
                            {
                                label: "View Positions",
                                onClick: () => handleViewPositions("locked"),
                            },
                        ],
                    },
                    buttons: {
                        primary: {
                            label: "Lock More",
                            onClick: () => handleLock(COMMON_TOKEN_IDS.USDC as LockTokenId),
                        },
                        secondary: {
                            label: "Increase Duration",
                            disabled: lockedPositions?.length <= 0,
                            onClick: () => handleIncreaseLockDuration(),
                        },
                    },
                },
            },
        ],
        [handleIncreaseLockDuration, handleLock, handleStake, handleStakeMore, lockedPositions],
    );

    return {
        cards,
        handleLock,
        handleStake,
        handleStakeMore,
        handleLockMore: handleLock,
        handleIncreaseLockDuration,
    };
};
